import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  PersonRemove as PersonRemoveIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import { useState } from "react";
import { AsyncSelectValue } from "../../components/AsyncSelect";
import PatientSelect from "../../components/PatientSelect";
import ProviderSelect from "../../components/ProviderSelect";
import { LobbyKnock } from "../types";
import {
  HOST_ASK_UNMUTE_LABEL,
  HOST_MUTE_LABEL,
  PARTICIPANT_AUDIO_LABEL,
} from "../telehealth-v2-constant";
import SpeakingIndicator from "./SpeakingIndicator";

export interface InviteRecipients {
  /** Patients receive a patient-specific URL (DOB-gated). */
  patients: AsyncSelectValue[];
  /** Providers receive the open public URL (anyone can join). */
  providers: AsyncSelectValue[];
}

interface Props {
  open: boolean;
  /** Whether THIS user is the host (drives admit/kick UI). */
  isHost: boolean;
  selfId?: number;
  participants: any[];
  knocks: LobbyKnock[];
  onAdmit: (userId: number) => void;
  onDeny: (userId: number) => void;
  onKick: (userId: number) => void;
  onClose: () => void;
  /** userIds currently talking; several may be active at once. */
  speakingIds?: ReadonlySet<number>;
  /** Host-only audio controls. Omit to hide the per-row mic button. */
  onMuteParticipant?: (userId: number) => void;
  onAskToUnmute?: (userId: number) => void;
  /** Host-only invite hook. When provided, an invite section renders at
   *  the bottom of the panel — the host picks recipients and the parent
   *  fans out the appropriate link kind to each. Omit to hide. */
  onInvite?: (recipients: InviteRecipients) => Promise<void>;
}

function audioLabelFor(p: any, speakingIds?: ReadonlySet<number>): string {
  if (speakingIds?.has(p.userId)) return PARTICIPANT_AUDIO_LABEL.SPEAKING;
  return p.muted
    ? PARTICIPANT_AUDIO_LABEL.MUTED
    : PARTICIPANT_AUDIO_LABEL.UNMUTED;
}

function isHostRow(p: any, selfId: number | undefined, selfIsHost: boolean) {
  // Trust the SDK flag when set; otherwise fall back to "self joined as host"
  // since some backends don't stamp the host role into the JWT.
  if (p.isHost) return true;
  if (selfIsHost && p.userId === selfId) return true;
  return false;
}

export default function ParticipantsPanel({
  open,
  isHost,
  selfId,
  participants,
  knocks,
  onAdmit,
  onDeny,
  onKick,
  onClose,
  speakingIds,
  onMuteParticipant,
  onAskToUnmute,
  onInvite,
}: Readonly<Props>) {
  // Local invite-bar state. Each picker is single-select; selections are
  // pushed onto a chip list so the host can fan one Send click into many
  // invitations.
  const [pendingPatient, setPendingPatient] = useState<AsyncSelectValue | null>(
    null
  );
  const [pendingProvider, setPendingProvider] =
    useState<AsyncSelectValue | null>(null);
  const [invitePatients, setInvitePatients] = useState<AsyncSelectValue[]>([]);
  const [inviteProviders, setInviteProviders] = useState<AsyncSelectValue[]>(
    []
  );
  const [sending, setSending] = useState(false);

  const addPatient = (val: AsyncSelectValue | null) => {
    if (!val) return;
    setInvitePatients((prev) =>
      prev.some((p) => p.value === val.value) ? prev : [...prev, val]
    );
    setPendingPatient(null);
  };
  const addProvider = (val: AsyncSelectValue | null) => {
    if (!val) return;
    setInviteProviders((prev) =>
      prev.some((p) => p.value === val.value) ? prev : [...prev, val]
    );
    setPendingProvider(null);
  };
  const removePatient = (uuid: string) =>
    setInvitePatients((prev) => prev.filter((p) => p.value !== uuid));
  const removeProvider = (uuid: string) =>
    setInviteProviders((prev) => prev.filter((p) => p.value !== uuid));

  const handleSend = async () => {
    if (!onInvite) return;
    if (invitePatients.length === 0 && inviteProviders.length === 0) return;
    setSending(true);
    try {
      await onInvite({
        patients: invitePatients,
        providers: inviteProviders,
      });
      setInvitePatients([]);
      setInviteProviders([]);
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;
  return (
    <Box
      sx={{
        width: 320,
        bgcolor: "background.paper",
        color: "text.primary",
        borderLeft: 1,
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
          }}
        >
          Participants
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Divider />

      {isHost && knocks.length > 0 && (
        <>
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              WAITING TO JOIN ({knocks.length})
            </Typography>
            <List dense disablePadding>
              {knocks.map((k) => (
                <ListItem key={k.userId} disableGutters sx={{ py: 0.5 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ width: 32, height: 32 }}>
                      {k.displayName?.[0]?.toUpperCase() ?? "?"}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={k.displayName}
                    slotProps={{
                      primary: { sx: { fontSize: 14 } },
                    }}
                  />
                  <Stack
                    direction="row"
                    sx={{
                      gap: 0.5,
                    }}
                  >
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      startIcon={<CheckIcon />}
                      onClick={() => onAdmit(k.userId)}
                      sx={{ minWidth: 0, px: 1.2 }}
                    >
                      Admit
                    </Button>
                    <IconButton
                      size="small"
                      onClick={() => onDeny(k.userId)}
                      sx={{ color: "text.secondary" }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </ListItem>
              ))}
            </List>
          </Box>
          <Divider />
        </>
      )}

      <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 1.5 }}>
        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          IN MEETING ({participants.length})
        </Typography>
        <List dense disablePadding>
          {participants.map((p) => (
            <ListItem key={p.userId} disableGutters sx={{ py: 0.5 }}>
              <ListItemAvatar>
                <Avatar sx={{ width: 32, height: 32 }}>
                  {p.displayName?.[0]?.toUpperCase() ?? "?"}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Stack
                    direction="row"
                    sx={{
                      gap: 0.75,
                      alignItems: "center",
                    }}
                  >
                    <span>{p.displayName}</span>
                    {isHostRow(p, selfId, isHost) && (
                      <Box
                        component="span"
                        sx={{
                          fontSize: 10,
                          px: 0.75,
                          py: "1px",
                          borderRadius: 0.5,
                          bgcolor: "primary.light",
                          color: "primary.dark",
                        }}
                      >
                        Host
                      </Box>
                    )}
                    {p.userId === selfId && (
                      <Box
                        component="span"
                        sx={{ fontSize: 11, color: "text.secondary" }}
                      >
                        (you)
                      </Box>
                    )}
                  </Stack>
                }
                secondary={
                  <Stack
                    component="span"
                    direction="row"
                    sx={{
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    {speakingIds?.has(p.userId) && (
                      <SpeakingIndicator label={p.displayName} size={10} />
                    )}
                    <span>{audioLabelFor(p, speakingIds)}</span>
                  </Stack>
                }
                slotProps={{
                  primary: { sx: { fontSize: 14 } },

                  secondary: {
                    component: "div",
                    sx: { fontSize: 12, color: "text.secondary" },
                  },
                }}
              />
              {isHost && p.userId !== selfId && (
                <Stack
                  direction="row"
                  sx={{
                    gap: 0.25,
                  }}
                >
                  {p.muted
                    ? onAskToUnmute && (
                        <Tooltip title={HOST_ASK_UNMUTE_LABEL}>
                          <IconButton
                            size="small"
                            aria-label={`${HOST_ASK_UNMUTE_LABEL}: ${p.displayName}`}
                            onClick={() => onAskToUnmute(p.userId)}
                            sx={{ color: "text.secondary" }}
                          >
                            <MicOffIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )
                    : onMuteParticipant && (
                        <Tooltip title={HOST_MUTE_LABEL}>
                          <IconButton
                            size="small"
                            aria-label={`${HOST_MUTE_LABEL}: ${p.displayName}`}
                            onClick={() => onMuteParticipant(p.userId)}
                            sx={{ color: "text.secondary" }}
                          >
                            <MicIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                  <IconButton
                    size="small"
                    aria-label={`Remove ${p.displayName}`}
                    onClick={() => onKick(p.userId)}
                    sx={{ color: "text.secondary" }}
                  >
                    <PersonRemoveIcon fontSize="small" />
                  </IconButton>
                </Stack>
              )}
            </ListItem>
          ))}
        </List>
      </Box>

      {isHost && onInvite && (
        <>
          <Divider />
          <Box sx={{ px: 2, py: 1.5 }}>
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                INVITE
              </Typography>
              <Button
                size="small"
                variant="contained"
                startIcon={<SendIcon fontSize="small" />}
                onClick={handleSend}
                disabled={
                  sending ||
                  (invitePatients.length === 0 && inviteProviders.length === 0)
                }
                sx={{ minWidth: 0, px: 1.2 }}
              >
                Send
              </Button>
            </Stack>

            <Stack
              sx={{
                gap: 1.5,
              }}
            >
              <Box>
                <PatientSelect
                  label="Patients (DOB-gated link)"
                  placeholder="Search patient"
                  value={pendingPatient as any}
                  onChange={(v) => addPatient(v as AsyncSelectValue | null)}
                  excludeUuids={invitePatients.map((p) => p.value)}
                />
                {invitePatients.length > 0 && (
                  <Stack
                    direction="row"
                    sx={{
                      flexWrap: "wrap",
                      gap: 0.5,
                      mt: 0.75,
                    }}
                  >
                    {invitePatients.map((p) => (
                      <Chip
                        key={p.value}
                        size="small"
                        label={p.label}
                        onDelete={() => removePatient(p.value)}
                      />
                    ))}
                  </Stack>
                )}
              </Box>

              <Box>
                <ProviderSelect
                  label="Providers (open public link)"
                  value={pendingProvider as any}
                  onChange={(v) => addProvider(v as AsyncSelectValue | null)}
                />
                {inviteProviders.length > 0 && (
                  <Stack
                    direction="row"
                    sx={{
                      flexWrap: "wrap",
                      gap: 0.5,
                      mt: 0.75,
                    }}
                  >
                    {inviteProviders.map((p) => (
                      <Chip
                        key={p.value}
                        size="small"
                        label={p.label}
                        onDelete={() => removeProvider(p.value)}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          </Box>
        </>
      )}
    </Box>
  );
}
